import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Alert, 
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  CheckCircleOutline
} from '@mui/icons-material';
import { gamoraApi, LoginRequest, SignupRequest } from '../services/api';
import { jwtDecode } from 'jwt-decode';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfUse from './TermsOfUse';
import AILiteracyDisclaimer from './AILiteracyDisclaimer';
import Logo from './Logo';

interface DecodedToken {
  user_id?: string;
  [key: string]: any;
}

const saveToken = (token: string) => {
  localStorage.setItem('gamora_token', token);
};

const UnifiedAuth: React.FC<{ onLogin: () => void }> = ({ onLogin }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termsOfUseOpen, setTermsOfUseOpen] = useState(false);
  const [aiDisclaimerOpen, setAiDisclaimerOpen] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const data: LoginRequest = { username, password };
      const res = await gamoraApi.login(data);
      if (res.access_token) {
        saveToken(res.access_token);
        if (res.role !== undefined) {
          localStorage.setItem('gamora_role', res.role.toString());
        }
        try {
          const decoded: DecodedToken = jwtDecode(res.access_token);
          if (decoded.user_id) {
            localStorage.setItem('gamora_user_id', decoded.user_id.toString());
          }
        } catch (e) {
          // ignore decode errors
        }
        onLogin();
      } else {
        setError('Invalid credentials');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      console.error('Error response:', err?.response);
      console.error('Error status:', err?.response?.status);
      console.error('Error data:', err?.response?.data);
      // Better error message extraction
      let errorMessage = 'Login failed';
      if (err?.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.response?.status === 401) {
        errorMessage = 'Invalid username or password';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const data: SignupRequest = { username, email, password, company };
      const res = await gamoraApi.signup(data);
      if (res.user_id) {
        setSuccess('Signup successful! You can now log in.');
        setIsLogin(true);
        setUsername('');
        setPassword('');
        setEmail('');
        setCompany('');
      } else {
        setError(res.message || 'Signup failed');
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = isLogin ? handleLogin : handleSignup;

  return (
    <Box sx={{ height: '100%', display: 'flex', bgcolor: '#fafafa' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, width: '100%', height: '100%' }}>
        {/* Left Column - Auth Form */}
        <Box sx={{ flex: '0 0 480px', maxWidth: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
          <Box sx={{ width: '100%', maxWidth: 360 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 4 }}>
                <Box sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '8px',
                  bgcolor: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  border: '1px solid #eee',
                }}>
                  <Logo size={22} />
                </Box>
                <Typography
                  sx={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: '#1a1a1a',
                    letterSpacing: '-0.3px',
                  }}
                >
                  Gamura
                </Typography>
              </Box>

              <Typography 
                variant="h5" 
                sx={{ 
                  textAlign: 'center', 
                  mb: 1, 
                  fontWeight: 700,
                  color: '#1a1a1a'
                }}
              >
                {isLogin ? 'Sign in' : 'Sign up'}
              </Typography>

              {/* Terms text for signup */}
              {!isLogin && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: 'center', 
                    mb: 3, 
                    color: '#666',
                    fontSize: 14
                  }}
                >
                                     By signing up, you agree to the{' '}
                   <Link 
                     href="#" 
                     onClick={(e) => {
                       e.preventDefault();
                       setTermsOfUseOpen(true);
                     }}
                     sx={{ color: '#1976d2', textDecoration: 'none' }}
                   >
                     Terms of use
                   </Link>
                   {' '}and{' '}
                   <Link 
                     href="#" 
                     onClick={(e) => {
                       e.preventDefault();
                       setPrivacyPolicyOpen(true);
                     }}
                     sx={{ color: '#1976d2', textDecoration: 'none' }}
                   >
                     Privacy Policy
                   </Link>
                   .{' '}
                   <Link 
                     href="#" 
                     onClick={(e) => {
                       e.preventDefault();
                       setAiDisclaimerOpen(true);
                     }}
                     sx={{ color: '#1976d2', textDecoration: 'none' }}
                   >
                     AI Literacy & Disclaimer
                   </Link>
                </Typography>
              )}

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              {/* Username field (for login) */}
              <TextField
                label={isLogin ? "Username" : "Email"}
                type="text"
                value={isLogin ? username : email}
                onChange={e => isLogin ? setUsername(e.target.value) : setEmail(e.target.value)}
                fullWidth
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fff',
                  }
                }}
              />

              {/* Username field (for signup only) */}
              {!isLogin && (
                <TextField
                  label="Username"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  fullWidth
                  margin="normal"
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fff',
                    }
                  }}
                />
              )}

              {/* Company field (for signup only) */}
              {!isLogin && (
                <TextField
                  label="Company Name"
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                  fullWidth
                  margin="normal"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fff',
                    }
                  }}
                />
              )}

              {/* Password field */}
              <TextField
                label="Password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth
                margin="normal"
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: '#fff',
                  }
                }}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: '#1a1a1a',
                  color: '#fff',
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: 16,
                  '&:hover': {
                    bgcolor: '#333',
                  }
                }}
                disabled={loading}
              >
                {loading ? (isLogin ? 'Signing in...' : 'Signing up...') : (isLogin ? 'Sign in' : 'Sign up')}
              </Button>

              {/* Forgot password link (login only) */}
              {isLogin && (
                <Box sx={{ textAlign: 'right', mb: 1 }}>
                  <Link 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      navigate('/forgot-password');
                    }}
                    sx={{ 
                      color: '#1976d2', 
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              )}

              {/* Toggle between login/signup */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {isLogin ? "Don't have an account? " : "Already have an account? "}
                  <Link 
                    href="#" 
                    onClick={(e) => {
                      e.preventDefault();
                      setIsLogin(!isLogin);
                      setError(null);
                      setSuccess(null);
                    }}
                    sx={{ 
                      color: '#1976d2', 
                      textDecoration: 'none',
                      fontWeight: 500,
                      '&:hover': {
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    {isLogin ? 'Sign up' : 'Sign in'}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Right Column - Features */}
        <Box sx={{ 
          flex: 1,
          display: { xs: 'none', md: 'flex' }, 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: '#fff',
          borderLeft: '1px solid #eceef1',
          px: 6,
        }}>
          <Box sx={{ maxWidth: 420 }}>
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                fontWeight: 700,
                color: '#1a1a1a',
                lineHeight: 1.3,
              }}
            >
              Built to Make Knowledge Accessible
            </Typography>

            <List sx={{ p: 0 }}>
              {[
                'Get accurate, AI-generated responses',
                'Built for compliance, policy, and internal data',
                'Secure, private, and always up-to-date',
                'Built to retrieve answers from your files, reports, and policies',
              ].map((text) => (
                <ListItem key={text} sx={{ px: 0, py: 1.2 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleOutline sx={{ color: '#16a34a', fontSize: 20 }} />
                  </ListItemIcon>
                  <ListItemText 
                    primary={text} 
                    primaryTypographyProps={{ fontSize: 14, color: '#555', lineHeight: 1.5 }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      </Box>

       {/* Privacy Policy Modal */}
       <PrivacyPolicy 
         open={privacyPolicyOpen} 
         onClose={() => setPrivacyPolicyOpen(false)} 
       />

       {/* Terms of Use Modal */}
       <TermsOfUse 
         open={termsOfUseOpen} 
         onClose={() => setTermsOfUseOpen(false)} 
       />

       {/* AI Literacy Disclaimer Modal */}
       <AILiteracyDisclaimer 
         open={aiDisclaimerOpen} 
         onClose={() => setAiDisclaimerOpen(false)} 
       />
     </Box>
   );
 };

export default UnifiedAuth; 